// src/SparepartsPages/Transit/CaseFileUpload.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Typography,
  TextField,
  Alert,
  Chip,
  Stack,
  Divider,
  Backdrop,
  LinearProgress,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { DataGrid } from "@mui/x-data-grid";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";

// ✅ react-toastify v5.5.0
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* =========================
   Helpers
   ========================= */
const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const CaseFileUpload = () => {
  // ✅ NOT A HOOK (safe anywhere)
  const userToken = LocalstorageHelper.GetUserToken();

  // toast spam guard (works even if App.js also has a ToastContainer)
  const TOAST_CID = "casefile";
  const lastToastMsgRef = useRef({});
  const pollTimerRef = useRef(null);

  const toastOnce = (type, message, toastId = "casefile-toast") => {
    if (!message) return;
    const opts = { containerId: TOAST_CID, toastId, autoClose: 2500 };
    if (toast.isActive(toastId)) {
      toast.update(toastId, {
        ...opts,
        render: message,
        type,
        isLoading: false,
      });
    } else {
      toast[type](message, opts);
    }
  };

  const toastOncePerMessage = (type, message, toastId) => {
    if (!message) return;
    const key = `${type}:${toastId || ""}`;
    if (lastToastMsgRef.current[key] === message) return;
    lastToastMsgRef.current[key] = message;
    toastOnce(type, message, toastId);
  };

  const copyText = async (text, toastId = "casefile-copy") => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
      toastOnce("success", "Copied to clipboard", toastId);
    } catch (e) {
      toastOnce(
        "error",
        "Copy failed (browser blocked clipboard)",
        `${toastId}-err`,
      );
    }
  };

  const handleCopyList = async (list, toastId = "casefile-copy") => {
    const text = Array.isArray(list) ? list.join("") : String(list || "");
    await copyText(text, toastId);
  };

  const handleCopyJson = async (obj) => {
    await copyText(JSON.stringify(obj ?? {}, null, 2), "casefile-copy-json");
  };

  const rowsToTSV = (rowsArr, colsArr) => {
    const cols = Array.isArray(colsArr) ? colsArr : [];
    const rows = Array.isArray(rowsArr) ? rowsArr : [];
    if (!cols.length || !rows.length) return "";
    const fields = cols.map((c) => c.field);
    const header = cols.map((c) => c.headerName || c.field).join("	");
    const body = rows
      .map((r) => fields.map((f) => (r?.[f] ?? "")).join("	"))
      .join("");
    return `${header}
${body}`;
  };

  const CopyableListGrid = ({ title, field, data, copyLabel, count }) => {
    const [sel, setSel] = useState([]);
    const total = Number.isFinite(Number(count)) ? Number(count) : (Array.isArray(data) ? data.length : 0);
    if (!Array.isArray(data) || !data.length) return null;

    const copyComma = async () => {
      const comma = (Array.isArray(data) ? data : []).join(",");
      if (!comma) return;
      await copyText(comma, "casefile-copy-comma");
    };

    const maxShow = 2000;
    const showList = data.slice(0, maxShow);

    const gridRows = showList.map((v, i) => ({
      id: i + 1,
      value: v,
    }));

    const gridCols = [
      {
        field: "value",
        headerName: field,
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <span style={{ fontFamily: "monospace" }}>{p.value}</span>
        ),
      },
    ];

    const copySelected = async () => {
      const selected = new Set(sel);
      const list = gridRows
        .filter((r) => selected.has(r.id))
        .map((r) => r.value);
      if (!list.length) return;
      await handleCopyList(list, "casefile-copy-selected");
    };

    return (
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" color="error">
            {title} ({total})
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={!sel.length}
              onClick={copySelected}
            >
              Copy Selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCopyList(data, "casefile-copy-all")}
            >
              {copyLabel || "Copy All"}
            </Button>
            <Button size="small" variant="outlined" onClick={copyComma}>
              Copy Comma
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ height: 320 }}>
          <DataGrid
            rows={gridRows}
            columns={gridCols}
            density="compact"
            checkboxSelection
            rowSelectionModel={sel}
            onRowSelectionModelChange={(m) => setSel(m)}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50, page: 0 } },
            }}
            sx={{
              borderRadius: 2,
              "& .MuiDataGrid-columnHeaders": { fontWeight: 800 },
            }}
          />
        </Box>

        {data.length > maxShow && (
          <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
            Showing first {maxShow}. Use Copy to get the full list.
          </Typography>
        )}
      </Box>
    );
  };

  const CopyableObjectGrid = ({ title, rows: rawRows, columns }) => {
    const [sel, setSel] = useState([]);
    const rowsArr = Array.isArray(rawRows) ? rawRows : [];
    if (!rowsArr.length || !Array.isArray(columns) || !columns.length)
      return null;

    const maxShow = 5000;
    const showRows = rowsArr.slice(0, maxShow).map((r, i) => ({
      id: i + 1,
      ...r,
    }));

    const copyAll = async () => {
      const tsv = rowsToTSV(showRows, columns);
      if (!tsv) return;
      await copyText(tsv, "casefile-copy-grid-all");
    };

    const copySelected = async () => {
      const selected = new Set(sel);
      const picked = showRows.filter((r) => selected.has(r.id));
      const tsv = rowsToTSV(picked, columns);
      if (!tsv) return;
      await copyText(tsv, "casefile-copy-grid-selected");
    };

    return (
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" color="error">
            {title} ({rowsArr.length})
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={!sel.length}
              onClick={copySelected}
            >
              Copy Selected
            </Button>
            <Button size="small" variant="outlined" onClick={copyAll}>
              Copy All (TSV)
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ height: 360 }}>
          <DataGrid
            rows={showRows}
            columns={columns}
            density="compact"
            checkboxSelection
            rowSelectionModel={sel}
            onRowSelectionModelChange={(m) => setSel(m)}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50, page: 0 } },
            }}
            sx={{
              borderRadius: 2,
              "& .MuiDataGrid-columnHeaders": { fontWeight: 800 },
            }}
          />
        </Box>

        {rowsArr.length > maxShow && (
          <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
            Showing first {maxShow}. Use Copy to get the full list.
          </Typography>
        )}
      </Box>
    );
  };

  /* =========================
     Hooks (MUST be unconditional)
     ========================= */
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [caseDate, setCaseDate] = useState(todayISO());

  const [rows, setRows] = useState([]);
  const [format, setFormat] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [totalQty, setTotalQty] = useState(0);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [warn, setWarn] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  const [job, setJob] = useState(null); // { jobId, status, progress, step, message, result }
  const [polling, setPolling] = useState(false);

  // ✅ show error details from backend (fail-fast)
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const [serverData, setServerData] = useState(null);
  const [needsOverwrite, setNeedsOverwrite] = useState(false);
  const [serverSel, setServerSel] = useState([]);

  const columns = useMemo(
    () => [
      { field: "casedate", headerName: "Case Date", width: 120 },
      { field: "dealercode", headerName: "Dealer Code", width: 140 },
      { field: "dealername", headerName: "Dealer Name", width: 260 },
      { field: "city", headerName: "City", width: 150 },
      { field: "invoicenumber", headerName: "Invoice No", width: 160 },
      { field: "deliverynumber", headerName: "Delivery No", width: 150 },
      { field: "casenumber", headerName: "Case/Box No", width: 170 },
      { field: "cartonbox", headerName: "Carton/Box To", width: 150 },
      { field: "partno", headerName: "Part No", width: 150 },
      { field: "partdescription", headerName: "Part Description", width: 360 },
      { field: "qty", headerName: "Qty", width: 90, type: "number" },
    ],
    [],
  );

  const previewRows = useMemo(
    () => rows.map((r, i) => ({ id: i + 1, ...r })),
    [rows],
  );

  const serverTableCols = useMemo(() => {
    if (!serverData) return [];

    if (serverData.type === "missing") {
      return [
        { field: "partno", headerName: "Part No", width: 160 },
        { field: "invoicenumber", headerName: "Invoice No", width: 160 },
        { field: "itemname", headerName: "Item Name", width: 320 },
      ];
    }

    if (serverData.type === "mismatch") {
      return [
        { field: "invno", headerName: "Inv No", width: 160 },
        { field: "partno", headerName: "Part No", width: 160 },
        { field: "qty", headerName: "Transit Qty", width: 140, type: "number" },
        {
          field: "caseqty",
          headerName: "Case Qty",
          width: 140,
          type: "number",
        },
      ];
    }

    if (serverData.type === "block") {
      return [{ field: "invoicenumber", headerName: "Invoice No", width: 200 }];
    }

    if (serverData.type === "transitMissing") {
      return [{ field: "invoicenumber", headerName: "Invoice No", width: 200 }];
    }

    return [];
  }, [serverData]);

  const serverTableRows = useMemo(() => {
    if (!serverData) return [];
    const arr =
      serverData.data ||
      serverData.mimatchedData ||
      serverData.mismatchedData ||
      [];
    if (!Array.isArray(arr)) return [];
    return arr.map((r, i) => ({ id: i + 1, ...r }));
  }, [serverData]);


  const errorCount = useMemo(() => {
    if (!errorDetails) return 0;
    const len = (v) => (Array.isArray(v) ? v.length : 0);
    return (
      len(errorDetails.transitMissingInvs) +
      len(errorDetails.blockedInvs) +
      len(errorDetails.existingInvs) +
      len(errorDetails.missingInvRows) +
      len(errorDetails.missingItemRows) +
      len(errorDetails.mismatchRows)
    );
  }, [errorDetails]);

  
  useEffect(() => {
    // reset selection when server validation data changes
    setServerSel([]);
  }, [serverData]);

/* =========================
     Functions
     ========================= */

  const stopPolling = () => {
    setPolling(false);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const getServerInvoiceField = () => {
    if (!serverData) return null;
    if (serverData.type === "mismatch") return "invno";
    // missing / block / transitMissing
    return "invoicenumber";
  };

  const copyServerAll = async () => {
    const tsv = rowsToTSV(serverTableRows, serverTableCols);
    if (!tsv) return;
    await copyText(tsv, "casefile-copy-server-all");
  };

  const copyServerSelected = async () => {
    if (!serverSel?.length) return;
    const selected = new Set(serverSel);
    const picked = serverTableRows.filter((r) => selected.has(r.id));
    const tsv = rowsToTSV(picked, serverTableCols);
    if (!tsv) return;
    await copyText(tsv, "casefile-copy-server-selected");
  };

  const copyServerInvoiceNos = async () => {
    const f = getServerInvoiceField();
    if (!f) return;
    const list = serverTableRows.map((r) => r?.[f]).filter(Boolean);
    const uniq = Array.from(new Set(list));
    if (!uniq.length) return;
    await handleCopyList(uniq, "casefile-copy-invnos");
  };

  const pollJob = async (jobId) => {
    try {
      const res = await fetch(`/casefile/transit/v2/import-status/${jobId}`, {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const data = await safeReadJson(res);

      if (!res.ok || data?.type === "error") {
        const msg = data?.message || "Job status failed";
        // if backend lost the job (restart / multi-instance), stop polling gracefully
        if (/job not found/i.test(msg)) {
          stopPolling();
          setError(msg);
          toastOncePerMessage("error", msg, "casefile-error");
          return;
        }
        throw new Error(msg);
      }

      setJob(data);

      const st = data.status;
      if (st === "done") {
        stopPolling();

        const r = data?.result || null;

        // ✅ New behavior:
        // - success: no errors
        // - partial: inserted valid invoices + returns error details for skipped rows/invoices
        // - error: nothing inserted (or hard failure)
        if (r?.type === "success") {
          setNeedsOverwrite(false);
          setServerData(null);
          setError("");
          setWarn("");
          setErrorDetails(null);
          setOpenErrorModal(false);
          setInfo(r.message || "Import Successful!");
          toastOnce("success", r.message || "Import Successful!", "casefile-success");
          return;
        }

        if (r?.type === "partial") {
          setNeedsOverwrite(false);
          setServerData(null);
          setError("");
          setWarn(r.message || "Partial Import completed with some errors.");
          setInfo(r.message || "Partial Import completed.");
          if (r.details) {
            setErrorDetails(r.details);
            setOpenErrorModal(true);
          }
          toastOncePerMessage("warning", r.message || "Partial Import completed.", "casefile-warning");
          return;
        }

        // backward compat / unexpected result types
        if (r?.type && r?.type !== "success") {
          const msg = r.message || "Import finished with errors.";
          setNeedsOverwrite(false);
          setServerData(null);
          setError(msg);
          if (r.details) {
            setErrorDetails(r.details);
            setOpenErrorModal(true);
          }
          toastOncePerMessage("error", msg, "casefile-error");
          return;
        }

        // generic done
        setInfo(data.message || "Done");
        toastOnce("success", data.message || "Done", "casefile-success");
      }
if (st === "failed" || st === "error") {
        stopPolling();
        const msg = data.error || data.message || "Import failed";
        setError(msg);
        // if backend sent details, keep them
        if (data.result && data.result.details) {
          setErrorDetails(data.result.details);
          setOpenErrorModal(true);
        }
        toastOncePerMessage("error", msg, "casefile-error");
      }
    } catch (e) {
      stopPolling();
      setError(e?.message || "Import failed");
      toastOncePerMessage(
        "error",
        e?.message || "Import failed",
        "casefile-error",
      );
    }
  };

  const startPolling = (jobId) => {
    // clear any existing poll loop
    stopPolling();
    setPolling(true);
    pollTimerRef.current = setInterval(() => pollJob(jobId), 1200);

    // immediately poll once
    pollJob(jobId);
  };

  const clearAll = () => {
    setFile(null);
    setFileName("");
    setCaseDate(todayISO());
    setRows([]);
    setFormat("");
    setRowCount(0);
    setTotalQty(0);
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setNeedsOverwrite(false);
    setJob(null);
    setErrorDetails(null);
    setOpenErrorModal(false);
    setServerSel([]);
    stopPolling();
    toastOnce("info", "Cleared.", "casefile-info");
  };

  const safeReadJson = async (res) => {
    // ✅ Works even if backend accidentally sends text/html
    const raw = await res.text();
    try {
      return raw ? JSON.parse(raw) : {};
    } catch {
      return { type: "error", message: raw || "Request failed" };
    }
  };

  const callPreview = async (pickedFile, dateISO) => {
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setNeedsOverwrite(false);
    setErrorDetails(null);
    setOpenErrorModal(false);

    if (!pickedFile) return;

    setLoadingPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      fd.append("caseDate", dateISO || "");

      const res = await fetch(`/casefile/transit/v2/preview`, {
        method: "POST",
        headers: { sptoken: userToken },
        body: fd,
      });

      const data = await safeReadJson(res);

      if (!res.ok || data?.type === "error") {
        throw new Error(data?.message || "Preview failed");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setFormat(data.format || "");
      setRowCount(Number(data.rowCount || 0));
      setTotalQty(Number(data.totalQty || 0));

      if (data.suggestedCaseDate) setCaseDate(data.suggestedCaseDate);

      const msg = `Preview ready. Format: ${data.format || "-"} | Rows: ${
        data.rowCount || 0
      }`;
      setInfo(msg);
      toastOnce("success", msg, "casefile-success");

      if (data.warning) {
        setWarn(data.warning);
        toastOncePerMessage("warning", data.warning, "casefile-warning");
      }
    } catch (e) {
      setRows([]);
      setFormat("");
      setRowCount(0);
      setTotalQty(0);
      const msg = e?.message || "Preview failed";
      setError(msg);
      toastOncePerMessage("error", msg, "casefile-error");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFilePick = async (picked) => {
    setFile(picked || null);
    setFileName(picked?.name || "");
    setRows([]);
    setFormat("");
    setRowCount(0);
    setTotalQty(0);
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setNeedsOverwrite(false);
    setErrorDetails(null);
    setOpenErrorModal(false);

    if (picked) {
      toastOnce(
        "info",
        "File selected. Generating preview...",
        "casefile-info",
      );
      await callPreview(picked, caseDate);
    }
  };

  const callImport = async ({ overwrite = false } = {}) => {
    stopPolling();
    setServerSel([]);
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setJob(null);
    setErrorDetails(null);
    setOpenErrorModal(false);

    if (!file) {
      const msg = "Choose a file first.";
      setError(msg);
      toastOncePerMessage("error", msg, "casefile-error");
      return;
    }
    if (!rows.length) {
      const msg = "No preview rows. Please Preview first.";
      setError(msg);
      toastOncePerMessage("error", msg, "casefile-error");
      return;
    }

    setLoadingImport(true);
    let stopFn = null;

    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caseDate", caseDate || "");
      fd.append("overwrite", overwrite ? "1" : "0");

      // ✅ ASYNC import (prevents 502 timeout)
      const res = await fetch(`/casefile/transit/v2/import-async`, {
        method: "POST",
        headers: { sptoken: userToken },
        body: fd,
      });

      const data = await safeReadJson(res);

      // immediate server validation responses
      if (data?.type === "overwrite") {
        setNeedsOverwrite(true);
        setServerData(data);
        const msg = data.message || "Invoice exists. Overwrite?";
        setWarn(msg);
        toastOncePerMessage("warning", msg, "casefile-warning");
        return;
      }


      // ✅ Partial / Success may be returned by SYNC endpoint (or if async route is changed)
      if (data?.type === "partial") {
        setNeedsOverwrite(false);
        setServerData(null);
        setError("");
        setWarn(data.message || "Partial Import completed.");
        setInfo(data.message || "Partial Import completed.");
        if (data?.details) {
          setErrorDetails(data.details);
          setOpenErrorModal(true);
        }
        toastOncePerMessage(
          "warning",
          data.message || "Partial Import completed.",
          "casefile-warning",
        );
        return;
      }

      if (data?.type === "success") {
        setNeedsOverwrite(false);
        setServerData(null);
        setError("");
        setWarn("");
        setErrorDetails(null);
        setOpenErrorModal(false);
        setInfo(data.message || "Import Successful!");
        toastOnce(
          "success",
          data.message || "Import Successful!",
          "casefile-success",
        );
        return;
      }
      if (
        ["missing", "mismatch", "block", "transitMissing"].includes(data?.type)
      ) {
        setNeedsOverwrite(false);
        setServerData(data);
        const msg = data.message || "Import validation failed.";
        setError(msg);
        toastOncePerMessage("error", msg, "casefile-error");
        if (data?.details) {
          setErrorDetails(data.details);
          setOpenErrorModal(true);
        }
        return;
      }

      if (!res.ok || data?.type === "error") {
        throw new Error(data?.message || "Import failed");
      }

      if (data?.type !== "accepted" || !data?.jobId) {
        throw new Error("Import didn't start (jobId missing).");
      }

      setJob({
        jobId: data.jobId,
        status: "queued",
        progress: 0,
        step: "Queued",
        message: "Queued",
      });
      toastOnce("info", "Import started. Please wait...", "casefile-info");

      // start polling status
      stopFn = startPolling(data.jobId);
    } catch (e) {
      const msg = e?.message || "Import failed";
      setError(msg);
      toastOncePerMessage("error", msg, "casefile-error");
      stopPolling();
    } finally {
      setLoadingImport(false);

      // if user navigates, interval cleanup happens via return in useEffect (we don't have one),
      // but we also stop when job becomes done/error. Keeping stopFn ref not needed now.
    }
  };

  /* =========================
     ✅ Redirect AFTER all hooks
     ========================= */
  if (!userToken) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      {/* If you already have ToastContainer in App.js, remove this line */}

      {/* ✅ Error Details Modal (fail-fast) */}
      <Dialog
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>Import Details</span>
            {errorCount > 0 && (
              <Chip size="small" color="error" label={`Errors: ${errorCount}`} />
            )}
          </Stack>
          <IconButton onClick={() => setOpenErrorModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Summary */}
          {(errorDetails?.insertedInvoices || errorDetails?.insertedRows || errorDetails?.skippedInvCount) ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Inserted: <b>{errorDetails?.insertedInvoices || 0}</b> invoice(s),{" "}
              <b>{errorDetails?.insertedRows || 0}</b> row(s).{" "}
              Skipped: <b>{errorDetails?.skippedInvCount || 0}</b> invoice(s).
            </Alert>
          ) : null}

          {/* Missing Transit Invoices */}
          <CopyableListGrid
            title="Missing Transit for Invoice(s)"
            field="Invoice No"
            data={errorDetails?.transitMissingInvs}
            copyLabel="Copy Invoices"
          />

          {/* Blocked Invoices */}
          <CopyableListGrid
            title="Blocked Invoice(s) (Purchase already done)"
            field="Invoice No"
            data={errorDetails?.blockedInvs}
            copyLabel="Copy Invoices"
          />

          {/* Existing Invoices (Overwrite needed) */}
          <CopyableListGrid
            title="Already Exists (Run with Overwrite to replace)"
            field="Invoice No"
            data={errorDetails?.existingInvs}
            copyLabel="Copy Invoices"
          />

          {/* Rows with Missing Invoice No */}
          <CopyableObjectGrid
            title="Rows with Missing Invoice Number"
            rows={errorDetails?.missingInvRows}
            columns={[
              { field: "sl", headerName: "Row", width: 80, type: "number" },
              { field: "invoicenumber", headerName: "Invoice No", width: 160 },
              { field: "casenumber", headerName: "Case/Box No", width: 170 },
              { field: "partno", headerName: "Part No", width: 150 },
              { field: "qty", headerName: "Qty", width: 100, type: "number" },
            ]}
          />

          {/* Rows with Missing Item Master */}
          <CopyableObjectGrid
            title="Rows with Missing Item Code in Master"
            rows={errorDetails?.missingItemRows}
            columns={[
              { field: "sl", headerName: "Row", width: 80, type: "number" },
              { field: "invoicenumber", headerName: "Invoice No", width: 160 },
              { field: "partno", headerName: "Part No", width: 160 },
            ]}
          />

          {/* Mismatch rows */}
          <CopyableObjectGrid
            title="Mismatch with Transit (Inv + Part)"
            rows={errorDetails?.mismatchRows}
            columns={[
              { field: "invno", headerName: "Inv No", width: 160 },
              { field: "partno", headerName: "Part No", width: 160 },
              { field: "transitqty", headerName: "Transit Qty", width: 140, type: "number" },
              { field: "fileqty", headerName: "File Qty", width: 140, type: "number" },
            ]}
          />
        </DialogContent>
      </Dialog>

      <ToastContainer
        containerId="casefile"
        position="top-right"
        autoClose={2500}
        newestOnTop
        limit={2}
      />

      <Sidebar
        Title={"CASE FILE TRANSIT (V2)"}
        expandedPanel={"panel4"}
        children={
          <Box sx={{ p: 2 }}>
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader
                title="CaseFile Upload & Import (V2)"
                subheader="New API | Company 1 = BAJAJ | Company 2 = TVS | Flexible header mapping supported"
              />
              <CardContent>
                <Backdrop
                  open={polling}
                  sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 10,
                  }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      width: { xs: "92%", sm: 520 },
                      maxWidth: 560,
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={22} />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Import Running
                        </Typography>
                      </Stack>

                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {job?.step || "Working..."}{" "}
                        {job?.message ? `— ${job.message}` : ""}
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={Number(job?.progress || 0)}
                      />

                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption">
                          Job:{" "}
                          {job?.jobId
                            ? String(job.jobId).slice(0, 14) + "..."
                            : "-"}
                        </Typography>
                        <Typography variant="caption">
                          {Number(job?.progress || 0)}%
                        </Typography>
                      </Stack>

                      <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        Don&apos;t close the tab while importing. (You can keep
                        it minimized)
                      </Typography>
                    </Stack>
                  </Paper>
                </Backdrop>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                      disabled={loadingPreview || loadingImport}
                    >
                      Choose File
                      <input
                        hidden
                        type="file"
                        accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={(e) => handleFilePick(e.target.files?.[0])}
                      />
                    </Button>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      {fileName ? `Selected: ${fileName}` : "No file selected"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Case Date (used if file has no date)"
                      type="date"
                      value={caseDate}
                      onChange={(e) => setCaseDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={loadingPreview || loadingImport}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={!file || loadingPreview || loadingImport}
                      onClick={() => callPreview(file, caseDate)}
                    >
                      {loadingPreview ? "Preview..." : "Preview"}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      flexWrap="wrap"
                    >
                      <Chip label={`Format: ${format || "-"}`} />
                      <Chip label={`Rows: ${rowCount || rows.length}`} />
                      <Chip label={`Total Qty: ${totalQty}`} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CloudUploadIcon />}
                        disabled={!file || !rows.length || loadingImport}
                        onClick={() => callImport({ overwrite: false })}
                      >
                        {loadingImport ? "Importing..." : "Import"}
                      </Button>

                      {needsOverwrite && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<WarningAmberIcon />}
                          disabled={!file || loadingImport}
                          onClick={() => callImport({ overwrite: true })}
                        >
                          {loadingImport
                            ? "Overwriting..."
                            : "Overwrite Import"}
                        </Button>
                      )}

                      {errorDetails && (
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<ErrorOutlineIcon />}
                          disabled={loadingPreview || loadingImport}
                          onClick={() => setOpenErrorModal(true)}
                        >
                          Errors{errorCount ? ` (${errorCount})` : ""}
                        </Button>
                      )}

                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={loadingPreview || loadingImport}
                        onClick={clearAll}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Grid>

                  {error && (
                    <Grid item xs={12}>
                      <Alert severity="error">{error}</Alert>
                    </Grid>
                  )}
                  {warn && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{warn}</Alert>
                    </Grid>
                  )}
                  {info && (
                    <Grid item xs={12}>
                      <Alert severity="success">{info}</Alert>
                    </Grid>
                  )}
                </Grid>

                <Box sx={{ mt: 2, height: 520 }}>
                  <DataGrid
                    rows={previewRows}
                    columns={columns}
                    density="compact"
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 50, page: 0 },
                      },
                    }}
                    sx={{
                      borderRadius: 2,
                      "& .MuiDataGrid-columnHeaders": { fontWeight: 800 },
                    }}
                  />
                </Box>

                {serverData && serverTableCols.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Server Validation Details
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1, flexWrap: "wrap" }}
                    >
                      <Chip size="small" label={`Rows: ${serverTableRows.length}`} />
                      <Button size="small" variant="outlined" onClick={copyServerAll}>
                        Copy All (TSV)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!serverSel?.length}
                        onClick={copyServerSelected}
                      >
                        Copy Selected
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={copyServerInvoiceNos}
                      >
                        Copy Invoice Nos
                      </Button>
                    </Stack>

                    <Box sx={{ height: 360 }}>
                      <DataGrid
                        rows={serverTableRows}
                        columns={serverTableCols}
                        density="compact"
                        checkboxSelection
                        rowSelectionModel={serverSel}
                        onRowSelectionModelChange={(m) => setServerSel(m)}
                        disableRowSelectionOnClick
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 50, page: 0 },
                          },
                        }}
                        sx={{
                          borderRadius: 2,
                          "& .MuiDataGrid-columnHeaders": { fontWeight: 800 },
                        }}
                      />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default CaseFileUpload;
